import { useDispatch } from "react-redux";

import type { BraintreeResponse, BraintreeSuccessResponse } from "@/types";
import { useMutation } from "@tanstack/react-query";
import {
  PurchaseSeatCheckoutPayload,
  findAndRetrieveEkataScoreApi,
} from "@/global/api/cart";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppStore } from "@/global/store";
import { selectCartManager } from "@/global/store/data/purchaseSeats/cartManager";
import { routes } from "@/global/routes";

import useGetSavedCard from "./useGetSavedCard";
import { useAccountingApis } from "./useAccountingApis";
import React, { useState } from "react";
import { setIsCheckoutLoading } from "@/global/store/data/purchaseSeats/checkout";
import { Box, Button, Grid, Stack, Typography, lighten } from "@mui/material";
import { useToggle } from "@/shared/base/hooks/useToggle";
import { Modal } from "@/shared/base/components/Modal/Modal";
import { verifyZipCode } from "@/global/utils/utils";
import { endpoints } from "@/global/api/endpoints";
import { selectUser } from "@/global/store/data/user";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { api } from "@/global/api/api";
import { CheckoutForm } from "./CheckoutForm";
import { ZipCodeErrorModal } from "../../../../GenericPaymentForm/ZipCodeErrorModal";
import { useFraudPrevention } from "@/global/context/FraudPreventionProvider";
import { useGetZipCodeLists } from "@/shared/modules/GenericPaymentForm/useGetZipCodeLists";
import {
  selectOrderLineItems,
  selectOrderServiceFee,
  selectServiceFeeTotal,
  selectServiceFeeTotalSeats,
} from "@/global/store/data/purchaseSeats/serviceFee";
import { ServiceFeeErrorModal } from "./ServiceFeeErrorModal";
import { useGoogleAnalytics } from "@/global/hooks/useGoogleAnalytics";
import { AdditionalDetails } from "./AdditionalDetails";
import { useGetCreditReport } from "./useGetCreditReport";
import { Scheduler } from "@/global/utils/scheduler";
import { vendorUUIDs } from "@/global/config/constants";
import { parse, format } from "date-fns";
import { useBraintree } from "@/global/hooks/useBraintree";
import constants from "@/global/config/constants";
import { usePaymentPlans } from "../PaymentPlans/usePaymentPlans";
import { PaymentPlans } from "../PaymentPlans/PaymentPlans";
import { useRepsProduct } from "../../../useRepsProduct";
import { useMetaPixelTracking } from "@/global/hooks/useMetaPixelTracking";
import { dispatchCustomEvent } from "@/global/utils/dispatchCustomEvent";
import { useTransactionErrorGenericModal } from "../../../modals/TransactionErrorGenericModal";
import { useRepInfoState } from "@/global/context/RepInfoCheckerContainer";
import { useInfoFields } from "@/global/context/InfoFieldsContainer";
import { useTimeoutWorker } from "../../../timeout-worker/useTimeoutWorker";
import { useStorageCache } from "@/global/hooks/useStorageCache";
import { useCheckout202ErrorHandler } from "@/shared/common/useCheckout202ErrorHandler";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { CheckoutSubtotalsV3 } from "./CheckoutSubtotals/CheckoutSubtotalsV3";
import { useCheckoutSubtotals } from "./CheckoutSubtotals/useCheckoutSubtotals";

const PaymentDetails = () => {
  const dispatch = useDispatch();
  const [addNewCard, setAddNewCard] = useState(false);
  const teamConfig = useTeamConfig();
  const {
    teamSchema,
    availableCreditEnabled,
    checkoutValidation,
    isSaveCardAllowed,
    purchaseSeatConfigs,
    platform,
    isBrainTreePaymentEnabled,
    uiConfigs,
    colors,
  } = useTeamConfig();

  const {
    updatePaymentFailCount,
    updateZipFailCount,
    handleAddAccountRestriction,
  } = useFraudPrevention();

  const router = useRouterExtended();
  const errorModalProps = useToggle();
  const zipCodeErrorModal = useToggle();
  const [errorMessage, setErrorMessage] = useState("");
  const transactionErrorGenericModal = useTransactionErrorGenericModal();
  const { timeoutWorker, isTimeoutEnabled } = useTimeoutWorker();
  const cartCache = useStorageCache(constants.cartCacheKey);

  // Payload data
  const cartState = useAppStore(selectCartManager);
  const { repName, repAccountId } = useRepInfoState();
  const { infoFields } = useInfoFields();

  /*
   +------------------------------------------------------------------------------+
   * [ apis ]
   +------------------------------------------------------------------------------+ */
  const { savedCard, getCard } = useGetSavedCard();
  const { getCheckoutCredit, addServiceFeeMutation, serviceFeeErrorModal } =
    useAccountingApis();
  const checkoutSubtotals = useCheckoutSubtotals();
  const creditReportApi = useGetCreditReport();
  const {
    paymentPlans,
    getPaymentPlans,
    selectedPaymentPlan,
    handleGetPaymentPlans,
    handleChangPaymentPlan,
  } = usePaymentPlans();
  const findAndRetrieveEkataScore = useMutation(findAndRetrieveEkataScoreApi);

  /*
   +------------------------------------------------------------------------------+
   * [ selectors ]
   +------------------------------------------------------------------------------+ */
  const user = useAppStore(selectUser);
  const checkoutCredit = useAppStore((s) => s?.data?.purchaseSeats?.credit);
  const { creditToUse, creditInLedger } = checkoutCredit;
  const serviceFeeTotalAmount = useAppStore(selectServiceFeeTotal);
  const selectServiceFeeTotalSeatCount = useAppStore(selectServiceFeeTotalSeats); // prettier-ignore
  const orderServiceFee = useAppStore(selectOrderServiceFee);
  const orderLineItems = useAppStore(selectOrderLineItems);
  const isPaymentDue = serviceFeeTotalAmount - creditToUse > 0;
  const { getZipCodeLists, zipCodeList } = useGetZipCodeLists();
  const { states: { recipientUser } } = useRepsProduct(); // prettier-ignore

  const { handleCheckBrokerEmail } = useFraudPrevention();
  const shouldCheckZipCode = teamConfig?.shouldCheckZipCode ?? true;

  // ------------------------------------------------
  // ref to keep track of analytics firing status
  // it helps to avoid duplicated firing
  const analyticsRef = React.useRef({ isFired: false });
  const {
    fireAnalyticsCheckoutCreated,
    fireAnalyticsCompletedCheckout,
    fireTeamAnalyticsCompletedCheckout,
  } = useGoogleAnalytics();
  const { metaPixelTrackSuccessPurchase } = useMetaPixelTracking();

  const { braintreePayment } = useBraintree();

  // checkout 202 error handler
  const { handleInventoryCheckout202Error } = useCheckout202ErrorHandler();

  const handleAddNewCard = (value: boolean) => {
    setAddNewCard(value);
  };

  const handleZipCodeValidation = (zipCode: string) => {
    const { allowedZipFailCount } = checkoutValidation;
    const { allowedZipCodes, restrictedZipCodes } = zipCodeList;
    let isValid = verifyZipCode(zipCode, allowedZipCodes, restrictedZipCodes);
    if (!isValid) {
      updateZipFailCount({
        onSuccess: async (invalidSubmitCount) => {
          dispatch(setIsCheckoutLoading(true));
          if (invalidSubmitCount > allowedZipFailCount) {
            await handleAddAccountRestriction("Invalid billing Zip code");
          } else {
            zipCodeErrorModal.onOpen();
          }
          dispatch(setIsCheckoutLoading(false));
        },
      });
    }
    return isValid;
  };

  const handlePaymentFail = async (
    error: any,
    isEkataPaymentFailed?: boolean
  ) => {
    dispatch(setIsCheckoutLoading(false));
    const { allowedPaymentFailCount } = checkoutValidation;
    updatePaymentFailCount({
      onSuccess: async (fraudSubmitCount) => {
        if (fraudSubmitCount > allowedPaymentFailCount) {
          await handleAddAccountRestriction("Purchase failures");
        } else {
          setErrorMessage(
            error?.response?.data?.error ||
              "Something went wrong, please try again or contact support if error persists"
          );
          if (isEkataPaymentFailed) {
            return transactionErrorGenericModal.set({
              errorMessage: error?.response?.data?.error,
              isOpen: true,
            });
          }

          errorModalProps.toggle();
        }
      },
    });
  };

  const parseAdditionalDetails = (data: any) => {
    if (purchaseSeatConfigs?.isCheckoutAdditionalDetailsEnabled) {
      return {
        companyName: data?.companyName,
        contractFirstName: data?.contractFirstName,
        contractLastName: data?.contractLastName,
        contractTitle: data?.contractTitle,
        contractEmail: data?.contractEmail,
        contractPhoneNumber: data?.contractPhoneNumber,
        isCateringSame: data?.isCateringSame,
        cateringName: data?.cateringName,
        cateringEmail: data?.cateringEmail,
        cateringPhone: data?.cateringPhone,
        disclaimerFullName: data?.disclaimerFullName,
        specialOccasion: data?.specialOccasion,
        contractAddress: data?.contractAddress,
      };
    }

    return {};
  };

  // prettier-ignore
  const onBraintreeSuccess = (response: BraintreeResponse): BraintreeSuccessResponse => {
    const paymentMethodNonce = response?.creditCards[0]?.nonce ?? undefined;
    const cardType = response?.creditCards[0]?.details?.cardType ?? undefined;
    return { paymentMethodNonce, cardType };
  };

  const onSubmit = async (data: any) => {
    // stopping the timer
    handleTimer("stop");

    // if (isPaymentDue && Number(creditPack) < dueAfterCredit) {
    //   setCreditPackErrorMessage("Please insert a valid amount here.");
    //   return;
    // } else {
    //   setCreditPackErrorMessage("");
    // }

    // handle zip code validation
    if (data?.zipCode && shouldCheckZipCode) {
      let isValidZipCode = handleZipCodeValidation(String(data?.zipCode));
      if (!isValidZipCode) return;
    }

    dispatch(setIsCheckoutLoading(true));

    const payload: PurchaseSeatCheckoutPayload = {
      teamSchema: teamSchema,
      // cartState data
      cartId: cartState.cartId,
      cart: cartState.cart.filter((item) => item.isHeld),
      allInPrice: serviceFeeTotalAmount,
      totalSeats: selectServiceFeeTotalSeatCount || cartState.totalSeats,
      sessionId: cartState?.sessionId,
      creditToUse: availableCreditEnabled ? checkoutCredit?.creditToUse : 0,
      orderServiceFee: orderServiceFee,
      orderLineItems: orderLineItems,
      repName: repName || undefined,
      repAccountId: repAccountId || undefined,
    };

    if (infoFields?.length > 0) payload.infoFields = infoFields;

    if (platform === vendorUUIDs.tickesdotcom) {
      payload.orderServiceFee = cartState.orderServiceFee as number;
    }

    if (paymentPlans.length > 0) {
      if (selectedPaymentPlan && selectedPaymentPlan.id !== 0) {
        payload.paymentPlans = {
          id: selectedPaymentPlan.id,
          title: selectedPaymentPlan.title,
          isDynamic: selectedPaymentPlan.isDynamic,
          description: selectedPaymentPlan.description,
          totalInstallments: selectedPaymentPlan.totalInstallments,
          installments: selectedPaymentPlan.installments,
        };
      }
    }

    if (addNewCard || (isPaymentDue && !savedCard.ccSeqId)) {
      payload.cardDetail = {
        cardNumber: data.cardNumber.replace(/\D/g, ""),
        cardCvc: data.cvv,
        cardExpiry: data?.cardExpiry.replace(/\/|\s/g, ""),
        address: data.streetAddress,
        zipCode: data.zipCode,
        firstName: data.firstName,
        lastName: data.lastName,
        saveCard: selectedPaymentPlan ? true : data.saveCard ? true : false, // forcing save card if payment plan selected
      };

      // tdc only
      if (platform === vendorUUIDs.tickesdotcom) {
        const isCreditAvailableBiggerThanDue =
          Math.max(creditInLedger, creditToUse) >
          serviceFeeTotalAmount - orderServiceFee;

        let isErrorOccurred = false;
        let braintreeSuccessResponse: BraintreeSuccessResponse | null = {
          cardType: null,
          paymentMethodNonce: null,
        };

        if (!isCreditAvailableBiggerThanDue && isBrainTreePaymentEnabled) {
          let paymentTokenData: any;
          try {
            const { data } = await api.post(
              endpoints.getPaymentToken,
              {
                teamSchema,
                cartId: cartState.cartId,
              },
              {
                headers: { Authorization: `Bearer ${user?.token}` },
              }
            );

            paymentTokenData = data;
          } catch (error: any) {
            handlePaymentFail(error);

            // resuming the timer
            handleTimer("resume");
            isErrorOccurred = true;
          }

          if (isErrorOccurred) return;

          const token = paymentTokenData?.paymentToken;
          const creditCard = {
            number: data.cardNumber,
            expirationDate: data?.cardExpiry,
            cvv: data.cvv,
            billingAddress: {
              postalCode: data.zipCode,
            },
          };

          if (token) {
            await braintreePayment({
              token,
              creditCard,
              onError: (error) => {
                isErrorOccurred = true;
                handlePaymentFail(error);
              },
              onSuccess: (response) =>
                (braintreeSuccessResponse = onBraintreeSuccess(response)),
            });
          }
        }

        if (isErrorOccurred) return;

        payload.cardDetail = {
          ...payload.cardDetail,
          ...braintreeSuccessResponse,
          cardExpiry: format(
            parse(data?.cardExpiry!, "MM/yy", new Date()),
            "yyyy-MM"
          ),
          city: data.city,
          country: "USA",
        };
      }

      // ekata validation
      try {
        const { data: ekataResponse } =
          await findAndRetrieveEkataScore.mutateAsync({
            teamSchema,
            cardDetail: {
              city: data.city,
              state: data.state,
              country: data.country,
              zipCode: data.zipCode,
              lastName: data.lastName,
              firstName: data.firstName,
              address: data.streetAddress,
            },
            user: {
              email: user?.email,
              userId: user?.userId,
              lastName: data.lastName,
              firstName: data.firstName,
              accountId: user?.accountId,
              phoneNumber: user?.phoneNumber,
              accountGroupIds: user?.accountGroupIds,
            },
          });

        if (!ekataResponse?.isValid)
          return handlePaymentFail(
            {
              response: {
                data: {
                  error: ekataResponse?.message,
                },
              },
            },
            true
          );
      } catch (error) {
        return handlePaymentFail(error);
      }
    } else if (!addNewCard && isPaymentDue && savedCard.ccSeqId) {
      payload.savedCard = {
        ccSeqId: savedCard.ccSeqId,
        ccType: savedCard.ccType,
      };
    }

    // order number from checkout response to be passed in insert additional details
    let orderNumber;

    // headers
    let headers: any = { Authorization: `Bearer ${user?.token}` };
    if (!!recipientUser) {
      headers = {
        ...headers,
        "Recipient-User-Email": recipientUser.email,
        "Recipient-User-Account-ID": recipientUser.accountId,
        "Recipient-User-Phone-Number": recipientUser.phoneNumber,
      };
    }

    if (purchaseSeatConfigs?.isCheckoutAdditionalDetailsEnabled) {
      payload.additionalDetails = parseAdditionalDetails(data);
    }

    try {
      const checkoutRes = await api.post(endpoints.checkoutCart, payload, {
        headers,
        timeout: 30000,
      });

      await creditReportApi.fetch(); // updating credit

      orderNumber = checkoutRes.data.orderNumber;
      fireAnalyticsCompletedCheckout(payload); // firing analytics event
      metaPixelTrackSuccessPurchase(payload); // firing meta pixel event
      fireTeamAnalyticsCompletedCheckout(payload, checkoutRes.data); // firing team analytics event
      dispatchCustomEvent(constants.customEvents.eventPurchaseSuccess, {
        cartRes: payload,
        checkoutRes: checkoutRes.data,
        user,
      });

      router.push({
        pathname: routes.checkoutStatus,
        query: {
          teamSchema,
          orderNumber,
          eventCode: payload?.cart[0]?.eventCode,
        },
      });

      // claring the cart cache from local storage
      cartCache.delete(teamSchema);
    } catch (error: any) {
      const { is202Error } = await handleInventoryCheckout202Error(error);
      if (is202Error) {
        return await router.pushSchema(
          teamConfig?.defaultRoute || routes.landing
        );
      }

      handlePaymentFail(error);

      // resuming the timer
      handleTimer("resume");
    } finally {
      dispatch(setIsCheckoutLoading(false));
    }
  };

  // handler to handle timer util
  function handleTimer(method: keyof Scheduler) {
    if (!isTimeoutEnabled) return;
    if (method == "start") timeoutWorker?.start();
    if (method == "stop") timeoutWorker?.stop();
    if (method == "resume") timeoutWorker?.resume();
    if (method == "reset") timeoutWorker?.reset();
  }

  React.useEffect(() => {
    handleTimer("start"); // starting the timer
    // checking broker initially in this form
    if (user) handleCheckBrokerEmail();
    return () => {
      // resetting the timer
      handleTimer("reset");
    };
  }, []);

  // firing analytics event
  React.useEffect(() => {
    if (!analyticsRef.current.isFired && cartState?.cartId) {
      analyticsRef.current.isFired = true;
      fireAnalyticsCheckoutCreated(cartState);
    }
  }, [cartState]);

  React.useEffect(() => {
    if (serviceFeeTotalAmount && !getCheckoutCredit.isLoading) {
      handleGetPaymentPlans(serviceFeeTotalAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceFeeTotalAmount, getCheckoutCredit.isLoading]);

  return (
    <>
      <ZipCodeErrorModal modalProps={zipCodeErrorModal} />
      <ServiceFeeErrorModal modalProps={serviceFeeErrorModal} />
      <Modal
        modalProps={errorModalProps}
        renderActions={() => (
          <Stack flexDirection={"row-reverse"}>
            <Button
              size="large"
              variant="contained"
              sx={{ minWidth: "120px" }}
              onClick={() => errorModalProps.toggle()}
            >
              Close
            </Button>
          </Stack>
        )}
      >
        <Box>
          <Typography>{errorMessage}</Typography>
        </Box>
      </Modal>

      <Box p={3} py={0}>
        {getCheckoutCredit.isLoading ||
        addServiceFeeMutation.isLoading ||
        getCard.isLoading ||
        getZipCodeLists.isLoading ||
        getPaymentPlans.isLoading ? (
          <Grid container spacing={3} mt={1}>
            {Array.from({ length: 4 })
              .fill(1)
              .map((_, idx) => {
                return (
                  <Grid item xs={12} key={idx}>
                    <DynamicSkeleton
                      rowCount={3}
                      spacing={1.5}
                      widths={["100%", "70%", "40%"]}
                      heights={["16px", "16px", "16px"]}
                    />
                  </Grid>
                );
              })}
          </Grid>
        ) : null}

        {!getCheckoutCredit.isLoading &&
        !addServiceFeeMutation.isLoading &&
        !getCard.isLoading &&
        !getZipCodeLists.isLoading &&
        !getPaymentPlans.isLoading ? (
          <Box mt={2}>
            <CheckoutSubtotalsV3 {...checkoutSubtotals} />

            <PaymentPlans
              paymentPlans={paymentPlans}
              onChange={handleChangPaymentPlan}
            />
          </Box>
        ) : null}

        {!getCheckoutCredit.isLoading &&
        !addServiceFeeMutation.isLoading &&
        !getCard.isLoading &&
        !getZipCodeLists.isLoading &&
        !getPaymentPlans.isLoading ? (
          <>
            <CheckoutForm
              onSubmit={onSubmit}
              savedCard={savedCard}
              addNewCard={addNewCard}
              onAddNewCard={handleAddNewCard}
              isSaveCardAllowed={isSaveCardAllowed}
              isPaymentDue={availableCreditEnabled ? isPaymentDue : true}
              disableSaveCard={
                !!selectedPaymentPlan && selectedPaymentPlan.id !== 0
              }
              // additional details base form, following render props pattern
              renderAdditionalBase={() => {
                if (uiConfigs?.checkoutPage?.checkoutDisclaimer) {
                  return (
                    <Box
                      p={1}
                      mt={2}
                      mb={1}
                      borderRadius={1}
                      bgcolor={lighten(colors.primary, 0.8)}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {uiConfigs?.checkoutPage?.checkoutDisclaimer}
                      </Typography>
                    </Box>
                  );
                }

                return purchaseSeatConfigs?.isCheckoutAdditionalDetailsEnabled ? (
                  <Box mt={2}>
                    <AdditionalDetails.BaseForm />
                  </Box>
                ) : null;
              }}
              // additional details disclaimer form, following render props pattern
              renderAdditionalDisclaimer={() => {
                // prettier-ignore
                if (uiConfigs?.checkoutPage?.checkoutForm?.checkoutTermsAndConditionsSecondary) {
                  return (
                    <Box mt={2}>
                      {uiConfigs?.checkoutPage?.checkoutForm?.checkoutTermsAndConditionsSecondary}
                    </Box>
                  );
                }

                return purchaseSeatConfigs?.isCheckoutAdditionalDetailsEnabled ? (
                  <Box mt={2}>
                    <AdditionalDetails.DisclaimerForm />
                  </Box>
                ) : null;
              }}
              paymentMethodTitle={paymentPlans.length > 0 ? "Card Details" : "Payment Method"} // prettier-ignore
              zipCodePlaceholder={uiConfigs?.checkoutPage?.zipCodePlaceholder}
            />
          </>
        ) : null}
      </Box>
    </>
  );
};

export default PaymentDetails;
